import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { UserBoard, UserBoardTypes, UserBoardComponentMode, UserBoardItem } from 'src/app/services/models/user-board.model';
import { map, Observable, startWith } from 'rxjs';
import { FormControl } from '@angular/forms';
import cloneDeep from 'lodash/cloneDeep';
import { FilterBar } from 'src/app/services/models/filter-bar.model';
import { FilterBarService } from 'src/app/services/filter-bar.service';
import { AddComponentToUserBoardItem } from 'src/app/services/models/user-board.model';

@Component({
  selector: 'bhub-view-boards',
  templateUrl: './view-boards.component.html',
  styleUrl: './view-boards.component.scss',
  standalone: false
})
export class ViewBoardsComponent implements OnInit, OnChanges {
  boardControl = new FormControl('');
  @Input() boards: UserBoard[] = []; // all available boards
  filteredBoards!: Observable<UserBoard[]>;
  selectedBoard?: UserBoard;
  filterBar: FilterBar = null;

  // Expose board types enum to template
  boardTypes = UserBoardTypes;

  constructor(
    private filterBarService: FilterBarService
  ) {}

  ngOnInit() {
    // Component initialization
  }

  ngOnChanges() {
    if (this.boards.length > 0) {
      this.setupBoardFiltering();
    }
  }

  private setupBoardFiltering(): void {
    this.filteredBoards = this.boardControl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value),
      map((name: string) => name ? this.boards
        .filter(b => b.userBoardName.toLowerCase().includes(name.toLowerCase()))
        .map((board) => this.prepareBoard(board))
        : this.boards.map((board) => this.prepareBoard(board))
      )
    );
  }

  private prepareBoard(board: UserBoard): UserBoard {
    // Clone the board to avoid mutating the original
    const preparedBoard = cloneDeep(board);
    
    // Handle legacy boards that might not have a board type set
    if (!preparedBoard.boardType) {
      preparedBoard.boardType = UserBoardTypes.TileBoard;
    }

    // For tile boards, ensure components are properly set up
    if (preparedBoard.boardType === UserBoardTypes.TileBoard && preparedBoard.itemList) {
      preparedBoard.itemList.forEach((item: UserBoardItem) => {
        // Set components to live mode for viewing
        if (item.data) {
          item.data.mode = UserBoardComponentMode.Live;
        }
        // Add component references if they're missing
        AddComponentToUserBoardItem(item);
      });
    }

    return preparedBoard;
  }

  onBoardSelected(event: any): void {
    const boardName = event.option.value;
    this.selectedBoard = this.boards.find(b => b.userBoardName === boardName);
    
    if (this.selectedBoard) {
      // Prepare the selected board
      this.selectedBoard = this.prepareBoard(this.selectedBoard);
      
      // Load filter bar if the board has one
      this.loadFilterBar();
    }
  }

  private loadFilterBar(): void {
    if (this.selectedBoard && this.selectedBoard.filterBarId) {
      this.filterBarService.getFilterBars().subscribe((filterBars: FilterBar[]) => {
        this.filterBar = filterBars.find((filterBar: FilterBar) => 
          filterBar.filterBarId === this.selectedBoard.filterBarId
        );
      });
    } else {
      this.filterBar = null;
    }
  }

  getBoardTypeLabel(boardType: UserBoardTypes): string {
    switch(boardType) {
      case UserBoardTypes.TileBoard:
        return 'Tile Board';
      case UserBoardTypes.SpreadsheetBoard:
        return 'Spreadsheet Board';
      default:
        return 'Tile Board'; // Default for legacy boards
    }
  }
}